import { HistoryStateEntry } from '@lexical/history';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { mergeRegister } from '@lexical/utils';
import { CAN_REDO_COMMAND, CAN_UNDO_COMMAND, COMMAND_PRIORITY_HIGH, COMMAND_PRIORITY_NORMAL, REDO_COMMAND, UNDO_COMMAND } from 'lexical';
import { cloneDeep, isEqual, omit } from 'lodash';
import React, { useEffect, useMemo } from 'react';

export type HistoryPluginChangeHandler = (values: { canUndo?: boolean; canRedo?: boolean }) => void;

// internal tags of lexical history plugin
export enum HISTORY_TAGS {
  HISTORIC = 'historic',
  MERGE = 'history-merge',
}

function isEqualWithoutProperties<T extends object>(
  a: T,
  b: T,
  propertiesToSkip: string[],
): boolean {
  return isEqual(omit(a, ...propertiesToSkip), omit(b, ...propertiesToSkip));
}

type HistoryEntry<T> = HistoryStateEntry & { extra: T };

export type HistoryWithExtraState<T> = {
  current: null | HistoryEntry<T>;
  redoStack: HistoryEntry<T>[];
  undoStack: HistoryEntry<T>[];
};

type Props<T> = {
  readonly extraState: T;
  readonly extraStateSetter: (val: T) => void;
  readonly mergeExtraStateKeys?: string[];
  readonly onChange?: HistoryPluginChangeHandler;
};

export function HistoryWithExtraStatePlugin<T extends object>(props: Props<T>): JSX.Element {
  const {
    extraState,
    extraStateSetter,
    mergeExtraStateKeys = [],
    onChange,
  } = props;

  const [editor] = useLexicalComposerContext();

  const historyState: HistoryWithExtraState<T> = useMemo(() => ({
    current: null,
    redoStack: [],
    undoStack: [],
  }), []);

  const cache = useMemo(() => ({
    extraState: cloneDeep(extraState),
    extraStateSetter,
  }), []);
  cache.extraStateSetter = extraStateSetter;

  useEffect(() => mergeRegister(
    editor.registerUpdateListener(({ tags }) => {
      const { current } = historyState;
      if (tags.has(HISTORY_TAGS.HISTORIC)) {
        return;
      }
      if (current && !current.extra) {
        current.extra = cache.extraState;
      }
    }),
    editor.registerCommand(
      UNDO_COMMAND,
      () => {
        const { undoStack, current } = historyState;
        const { extra } = undoStack.at(-1) ?? {};
        if (extra && !isEqual(extra, current?.extra)) {
          cache.extraState = extra;
          cache.extraStateSetter(extra);
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    ),
    editor.registerCommand(
      REDO_COMMAND,
      () => {
        const { redoStack, current } = historyState;
        const { extra } = redoStack.at(-1) ?? {};

        if (extra && !isEqual(extra, current?.extra)) {
          cache.extraState = extra;
          cache.extraStateSetter(extra);
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    ),
  ), [editor, historyState]);

  useEffect(() => {
    // We check if the extra state has changed in a meaningful way before updating the history state.
    // We ignore changes that only affect properties listed in mergeExtraStateKeys or 'cellHeight',
    // as these are expected to change frequently and we don't want them to create new history entries.
    if (isEqualWithoutProperties(cache.extraState, extraState, [...mergeExtraStateKeys, 'cellHeight'])) {
      return;
    }

    // Save the currently focused element before updating state
    // This is necessary because updating the editor state can cause the focus to be lost, which can disrupt the user's workflow.
    // For example, if the user is typing changing left padding and the state updates,
    // we want to ensure they can continue typing without having to manually refocus the editor.
    const activeElement = document.activeElement as HTMLElement | null;

    // Creates a new history entry with the updated extra state.
    // We clone the current editor state to ensure that we are not mutating the existing state, which could lead to unexpected behavior.
    // Setting state move focus to the end of the editor, so we restore it to the previously active element after the state update.
    cache.extraState = extraState;
    const currentState = editor.getEditorState().clone();
    editor.setEditorState(currentState, { tag: 'extra-history' });

    // Restore focus to the element that was active before the state update
    // setEditorState() can cause focus to be lost. We restore it to maintain the user's
    // workflow, whether they were editing in the editor or working in another panel.
    if (activeElement && typeof activeElement.focus === 'function') {
      activeElement.focus();
    }
  }, [extraState]);

  useEffect(() => {
    onChange?.({
      canUndo: Boolean(historyState.undoStack.length),
      canRedo: Boolean(historyState.redoStack.length),
    });
  }, [historyState, onChange]);

  useEffect(() => {
    if (!onChange) {
      return;
    }

    return mergeRegister(
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (canUndo) => {
          onChange({ canUndo });

          return false;
        },
        COMMAND_PRIORITY_NORMAL,
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (canRedo) => {
          onChange({ canRedo });

          return false;
        },
        COMMAND_PRIORITY_NORMAL,
      ),
    );
  }, [editor, onChange]);

  return <HistoryPlugin externalHistoryState={historyState} />;
}

